import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useRef, useState } from "react";

import { staffApi } from "@bw/shared/api";
import type { BookingParticipantResponse } from "@bw/shared/types/generated";

import type { Booking } from "@/libs/bookingsApi";

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function participantIdentity(participant: BookingParticipantResponse): string {
  const resolved = participant.display_name?.trim();
  return resolved && resolved !== participant.user_id.trim() ? resolved : "—";
}

export function BookingRosterDrawer({
  facilityId,
  booking,
  onOpenChange,
  onClosed,
}: {
  facilityId: string;
  booking: Booking | null;
  onOpenChange: (open: boolean) => void;
  onClosed: () => void;
}) {
  const bookingId = booking?.booking_id ?? null;
  const [participants, setParticipants] = useState<
    BookingParticipantResponse[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  const rosterRequestGeneration = useRef(0);
  const targetKey = bookingId ? `${facilityId}:${bookingId}` : null;
  const currentTargetKey = useRef(targetKey);
  currentTargetKey.current = targetKey;

  const load = useCallback(async () => {
    if (!bookingId) return;
    const requestTargetKey = `${facilityId}:${bookingId}`;
    if (currentTargetKey.current !== requestTargetKey) return;
    const requestGeneration = ++rosterRequestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      const roster = await staffApi.getBookingCheckInStatus(
        facilityId,
        bookingId,
      );
      if (
        rosterRequestGeneration.current !== requestGeneration ||
        currentTargetKey.current !== requestTargetKey
      ) {
        return;
      }
      setParticipants(roster.participants ?? []);
    } catch (caught) {
      if (
        rosterRequestGeneration.current !== requestGeneration ||
        currentTargetKey.current !== requestTargetKey
      ) {
        return;
      }
      setError(errorMessage(caught, "Failed to load the roster."));
      setParticipants([]);
    } finally {
      if (
        rosterRequestGeneration.current === requestGeneration &&
        currentTargetKey.current === requestTargetKey
      ) {
        setLoading(false);
      }
    }
  }, [bookingId, facilityId]);

  useEffect(() => {
    rosterRequestGeneration.current += 1;
    setParticipants([]);
    setError(null);
    setMarkError(null);
    setMarking(null);
    if (!bookingId) {
      setLoading(false);
      return;
    }
    void load();
    return () => {
      rosterRequestGeneration.current += 1;
    };
  }, [bookingId, load]);

  const handleCheckIn = async (userId: string) => {
    if (!bookingId || marking !== null) return;
    const mutationTargetKey = `${facilityId}:${bookingId}`;
    setMarking(userId);
    setMarkError(null);
    try {
      await staffApi.manualCheckInParticipant(facilityId, bookingId, userId);
      if (currentTargetKey.current !== mutationTargetKey) return;
      await load();
    } catch (caught) {
      if (currentTargetKey.current !== mutationTargetKey) return;
      setMarkError(
        errorMessage(caught, "Failed to check in this participant."),
      );
    } finally {
      if (currentTargetKey.current === mutationTargetKey) {
        setMarking(null);
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={booking !== null}
      onClose={() => onOpenChange(false)}
      slotProps={{
        transition: { onExited: onClosed },
        paper: {
          role: "dialog",
          "aria-labelledby": "booking-roster-title",
          sx: { width: { xs: "100%", sm: 480 }, maxWidth: "100%" },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography id="booking-roster-title" variant="h6">
              Check-in roster
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mark each participant present as they arrive.
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={() => onOpenChange(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack spacing={2} sx={{ p: 2, overflowY: "auto" }}>
          {markError ? <Alert severity="error">{markError}</Alert> : null}

          {loading ? (
            <Stack
              spacing={1}
              role="status"
              aria-label="Loading booking roster"
            >
              <Skeleton variant="rounded" height={44} />
              <Skeleton variant="rounded" height={44} />
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : participants.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              role="status"
              sx={{ py: 4, textAlign: "center" }}
            >
              No participants on this booking yet.
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label="Check-in roster">
                <TableHead>
                  <TableRow>
                    <TableCell>Participant</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map((participant) => {
                    const checkedIn = Boolean(participant.checked_in_at);
                    return (
                      <TableRow
                        key={participant.user_id}
                        data-slot="roster-row"
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {participantIdentity(participant)}
                        </TableCell>
                        <TableCell>
                          {participant.role === "host" ? (
                            <Chip
                              label="Host"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Attendee
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {checkedIn ? (
                            <Chip
                              label={`Checked in${
                                participant.check_in_method
                                  ? ` · ${participant.check_in_method}`
                                  : ""
                              }`}
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                void handleCheckIn(participant.user_id)
                              }
                              disabled={marking !== null}
                              sx={{
                                "&.Mui-disabled": { pointerEvents: "auto" },
                              }}
                            >
                              {marking === participant.user_id
                                ? "Checking in…"
                                : "Check in"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
}
